// Dedicated UI Store - Phase 3: State Management Optimization
import { create } from 'zustand';
import { persist, subscribeWithSelector, devtools } from 'zustand/middleware';

interface Notification {
  id: string;
  type: 'success' | 'error' | 'warning' | 'info';
  title: string;
  message?: string;
  timestamp: number;
}

interface UIState {
  theme: 'light' | 'dark' | 'system';
  sidebarCollapsed: boolean;
  notifications: Notification[];
}

interface UIActions {
  setTheme: (theme: 'light' | 'dark' | 'system') => void;
  toggleSidebar: () => void;
  addNotification: (notification: Omit<Notification, 'id' | 'timestamp'>) => void;
  removeNotification: (id: string) => void;
  clearNotifications: () => void;
}

type UIStore = UIState & UIActions;

export const useUIStore = create<UIStore>()(
  devtools(
    subscribeWithSelector(
      persist(
        (set, get) => ({
          // Initial state
          theme: 'system',
          sidebarCollapsed: false,
          notifications: [],

          // Actions
          setTheme: (theme) => {
            set({ theme });
            if (theme === 'system') {
              const prefersDark = window.matchMedia('(prefers-color-scheme: dark)').matches;
              document.documentElement.classList.toggle('dark', prefersDark);
            } else {
              document.documentElement.classList.toggle('dark', theme === 'dark');
            }
          },

          toggleSidebar: () => {
            set((state) => ({ sidebarCollapsed: !state.sidebarCollapsed }));
          },

          addNotification: (notification) => {
            const id = `notification-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;
            const newNotification = {
              ...notification,
              id,
              timestamp: Date.now(),
            };

            set((state) => ({
              notifications: [newNotification, ...state.notifications].slice(0, 10)
            }));

            if (notification.type !== 'error') {
              setTimeout(() => {
                get().removeNotification(id);
              }, 5000);
            }
          },

          removeNotification: (id) => {
            set((state) => ({
              notifications: state.notifications.filter(n => n.id !== id)
            }));
          },

          clearNotifications: () => {
            set({ notifications: [] });
          },
        }),
        {
          name: 'ui-storage',
          partialize: (state) => ({
            theme: state.theme,
            sidebarCollapsed: state.sidebarCollapsed,
          }),
        }
      )
    ),
    { name: 'UI Store' }
  )
);

// Optimized selectors
export const useTheme = () => useUIStore((state) => state.theme);
export const useSidebarCollapsed = () => useUIStore((state) => state.sidebarCollapsed);
export const useNotifications = () => useUIStore((state) => state.notifications);